import { Injectable } from '@angular/core';
import { HttpServiceBundle } from './base/http-service-bundle.service';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BaseHttpWithBundleService } from '@app/core/services/base/base-service-with-bundle.service';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { Router, RoutesRecognized } from '@angular/router';
import { DRResponse } from '@app/shared/models/response';
import { map } from 'rxjs/operators';
import * as moment from 'moment';
import { AuthenticationService } from '@app/core/authentication';
import { SessionType } from '@app/shared/models/session-type';

export interface SessionPageResponse {
    code: string;
    supportAccess: { expiration: string };
}

@Injectable({
    providedIn: 'root'
})
export class SessionService extends BaseHttpWithBundleService {

    protected clientUri: string;

    // Just a property that is set so nobody removes the 'appearing' not used inject service from app.component.ts
    public sendSession = false;
    public supportSessionExpiration: Date | false;
    protected supportSessionTimeoutId: number;

    constructor(httpServiceBundle: HttpServiceBundle, protected router: Router, protected authenticationService: AuthenticationService) {
        super(httpServiceBundle, true);
        this.uri += `/${BackendUriUnits.CLIENTS}`;


        this.router.events.subscribe((data) => {
            if (this.sendSession && data instanceof RoutesRecognized && this.authenticationService.currentUserValue) {
                const routeData = data.state.root.firstChild.data;

                if (routeData && !routeData.hideToolbar) {
                    // Send the session post except for the pages that do not contain a toolbar since this means nobody is authenticated:
                    // - login page
                    // - confirm or unlock account page if they have additional params
                    // - inSign pages
                    const pageUrl = window.location.href;
                    this.sendSessionLastPage(pageUrl)
                        .subscribe((result: any) => {
                            this.setSupportSessionTimer(result && result.supportAccess && result.supportAccess.expiration);
                        }, err => this.handleError(err));
                }
            }
        });
    }

    public get isSupportSessionActive(): boolean {
        return this.authenticationService.userIsSupportUser && this.authenticationService.sessionType == SessionType.SUPPORT 
    }

    protected setSupportSessionTimer(expiration): void {
        this.supportSessionExpiration = expiration ? new Date(expiration) : false;

        if (this.supportSessionTimeoutId || !this.supportSessionExpiration) {
            clearTimeout(this.supportSessionTimeoutId);
        }

        if (this.supportSessionExpiration) {
            const millisecondsTillExpiration = moment((<Date>this.supportSessionExpiration)).diff(moment(), 'milliseconds');
            this.supportSessionTimeoutId = setTimeout(() => {
                this.supportSessionExpiration = null;
            }, millisecondsTillExpiration);
        }
    }

    protected sendSessionLastPage(page: string): Observable<SessionPageResponse> {
        const uri = `${this.uri}/${BackendUriUnits.SESSION}`;
        return this.http.post<SessionPageResponse>(uri, { lastPage: page });
    }

    public allowSupportAccess(expiration: Date): Observable<Date | false> {
        const uri = `${this.uri}/${BackendUriUnits.SUPPORT_ACCESS}`;
        return this.http.post<DRResponse<{ expiration: string }>>(uri, { expiration: expiration })
                        .pipe(map(result => {
                            this.setSupportSessionTimer(result && result.data && result.data.expiration);
                            return this.supportSessionExpiration;
                        }));
    }

    public cancelSupportAccess(): Observable<any> {
        const uri = `${this.uri}/${BackendUriUnits.SUPPORT_ACCESS}`;
        return this.http.delete<DRResponse<any>>(uri)
                        .pipe(map(result => {
                            this.setSupportSessionTimer(false);
                            return this.supportSessionExpiration;
                        }));
    }
}
